import { signOut, useSession } from "next-auth/react";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { useEffect, useState, createContext, useContext } from "react";

import { AiAssistantSsoService } from "@calcom/lib/agents/aiAssistant/aiAssistantSsoService";
import { usePmsId } from "@calcom/lib/hooks/usePmsId";

// Context for managing slideout state and header z-index
const SlideoutContext = createContext({
  isSlideoutOpen: false,
  setIsSlideoutOpen: (open: boolean) => {},
  isSidebarCollapsed: false,
  setIsSidebarCollapsed: (collapsed: boolean) => {},
  isFullWindowOpen: false,
  setIsFullWindowOpen: (open: boolean) => {},
});

// Context for managing focus mode
type FocusMode = "Consultations" | "Catch-ups";

interface FocusModeContextType {
  focusMode: FocusMode;
  setFocusMode: (mode: FocusMode) => void;
}

const FocusModeContext = createContext<FocusModeContextType>({
  focusMode: "Consultations",
  setFocusMode: () => {},
});

export const useFocusMode = () => useContext(FocusModeContext);

export const FocusModeProvider = ({ children }: { children: React.ReactNode }) => {
  const [focusMode, setFocusMode] = useState<FocusMode>("Consultations");

  return (
    <FocusModeContext.Provider value={{ focusMode, setFocusMode }}>
      {children}
    </FocusModeContext.Provider>
  );
};

export const useSlideoutContext = () => useContext(SlideoutContext);

export const SlideoutProvider = ({ children }: { children: React.ReactNode }) => {
  const [isSlideoutOpen, setIsSlideoutOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isFullWindowOpen, setIsFullWindowOpen] = useState(false);

  return (
    <SlideoutContext.Provider value={{
      isSlideoutOpen,
      setIsSlideoutOpen,
      isSidebarCollapsed,
      setIsSidebarCollapsed,
      isFullWindowOpen,
      setIsFullWindowOpen
    }}>
      {children}
    </SlideoutContext.Provider>
  );
};

// Utility function to get cookie value
const getCookie = (name: string): string | null => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    const cookieValue = parts.pop()?.split(';').shift();
    return cookieValue || null;
  }
  return null;
};

// Utility function to clear a cookie
const clearCookie = (name: string): void => {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
};

const RaiqaSlideout = () => {
  const { isSlideoutOpen, setIsSlideoutOpen, isFullWindowOpen, setIsFullWindowOpen } = useSlideoutContext();
  const [iframeUrl, setIframeUrl] = useState<string>("");
  const [shouldReprocessUrl, setShouldReprocessUrl] = useState(false);
  const [iframeLoading, setIframeLoading] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [isAuthFailed, setIsAuthFailed] = useState(false);
  const [lastRetryTime, setLastRetryTime] = useState(0);
  const { data: user } = useMeQuery();

  // Request microphone permissions for the parent domain
  const requestMicrophonePermissions = async () => {
    try {
      if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
        // Request permissions on the parent domain to establish trust
        await navigator.mediaDevices.getUserMedia({ audio: true });
        console.log('Microphone permissions granted on parent domain');
      }
    } catch (error) {
      console.log('Microphone permission request on parent domain:', error);
      // Don't block the iframe loading if parent permission fails
    }
  };

  const MAX_RETRIES = 3;
  const RETRY_COOLDOWN = 5000; // 5 seconds

  // Reset auth state when slideout is opened
  useEffect(() => {
    if (isSlideoutOpen) {
      // Reset auth failure state when opening slideout
      setIsAuthFailed(false);
      setRetryCount(0);
      setLastRetryTime(0);
    }
  }, [isSlideoutOpen]);

    // PRACTICAL APPROACH TO DETECT IFRAME LOGIN REDIRECTS
  // Instead of trying to access iframe's document URL (blocked by cross-origin),
  // we use these simple, reliable methods:
  // 1. Check session validity via API before loading iframe
  // 2. Use iframe onError event to detect loading failures
  // 3. Use loading timeout to detect slow loads (often indicates login redirect)
  // 4. Listen for postMessage from iframe if it cooperates
  // 5. Periodic session checks to catch expiry

  const checkSessionValidity = async () => {
    try {
      const response = await fetch('/api/ai-assistant/check-session', {
        method: 'GET',
        credentials: 'include'
      });

      if (!response.ok) {
        console.log('Session check failed, will trigger SSO flow');
        return false;
      }

      // Reset retry count on successful session check
      setRetryCount(0);
      setIsAuthFailed(false);
      return true;
    } catch (error) {
      console.log('Session check error:', error);
      return false;
    }
  };

  const handleAuthFailure = () => {
    const now = Date.now();

    // Check if we're within cooldown period
    if (now - lastRetryTime < RETRY_COOLDOWN) {
      console.log('Still in cooldown period, skipping retry');
      return;
    }

    // Check if we've exceeded max retries
    if (retryCount >= MAX_RETRIES) {
      console.log('Max retries exceeded, stopping authentication attempts');
      setIsAuthFailed(true);
      return;
    }

    console.log(`Authentication failed, retry ${retryCount + 1}/${MAX_RETRIES}`);
    setRetryCount(prev => prev + 1);
    setLastRetryTime(now);
    setShouldReprocessUrl(true);
  };

    useEffect(() => {
    const getIframeUrl = async () => {
      if (isSlideoutOpen && !isAuthFailed) {
        try {
          // Check if we should reprocess the URL
          if (shouldReprocessUrl) {
            console.log('Reprocessing URL, clearing cookies...');
            clearCookie('ai-assistant-session-expiry');
            clearCookie('ai-assistant-workspace-slug');
            setShouldReprocessUrl(false);
          }

          // Check session validity before loading iframe
          const isSessionValid = await checkSessionValidity();
          if (!isSessionValid) {
            console.log('Session invalid, will use SSO flow');
          }

          // Check if we have a valid session cookie first
          const expiryDateString = getCookie('ai-assistant-session-expiry');

          if (expiryDateString && !shouldReprocessUrl) {
            try {
              // Parse timestamp from cookie
              const expiryTimestamp = parseInt(expiryDateString, 10);
              const now = new Date();

              if (now.getTime() < expiryTimestamp) {
                // Session still valid - use direct URL with workspace slug from user data
                const workspaceSlug = user?.aiAssistantWorkspaceSlug || getCookie('ai-assistant-workspace-slug') || 'raiqa-health';
                const directUrl = `${process.env.NEXT_PUBLIC_AI_ASSISTANT_URL || 'https://ai.raiqahealth.au'}/workspace/${workspaceSlug}?hideHeader=true`;
                setIframeUrl(directUrl);
                setIframeLoading(true);
                return;;
              } else {
                // Session expired, call SSO API
                const ssoService = new AiAssistantSsoService();
                const redirectUrl = await ssoService.getRedirectUrl();
                setIframeUrl(redirectUrl);
                setIframeLoading(true);
                return;
              }
            } catch (dateError) {
              // Error parsing timestamp, fall through to SSO API call
            }
          }

          // Cookie missing or expired - call SSO API
          const ssoService = new AiAssistantSsoService();
          const redirectUrl = await ssoService.getRedirectUrl();
          setIframeUrl(redirectUrl);
          setIframeLoading(true);
        } catch (error) {
          console.log('Error getting iframe URL:', error);
          handleAuthFailure();
        }
      }
    };

    getIframeUrl();
  }, [isSlideoutOpen, user?.aiAssistantWorkspaceSlug, shouldReprocessUrl, retryCount, isAuthFailed]);

    // Simple iframe monitoring and session management
  useEffect(() => {
    if (!isSlideoutOpen || isAuthFailed) return;

    // Simple message listener for iframe communication
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== process.env.NEXT_PUBLIC_AI_ASSISTANT_URL || 'https://ai.raiqahealth.au') return;

      if (event.data && event.data.type === 'login-redirect') {
        console.log('Received login redirect message from iframe');
        handleAuthFailure();
      }
    };

    // Simple periodic session check (only if not in failed state)
    const checkSession = async () => {
      if (isAuthFailed || retryCount >= MAX_RETRIES) return;

      const isValid = await checkSessionValidity();
      if (!isValid) {
        console.log('Session expired, will trigger reauth');
        handleAuthFailure();
      }
    };

    // Timeout mechanism to detect slow loading (possible login redirect)
    const setupLoadingTimeout = () => {
      if (iframeLoading && !isAuthFailed) {
        const timeoutId = setTimeout(() => {
          console.log('Iframe loading timeout, possible login redirect');
          handleAuthFailure();
          setIframeLoading(false);
        }, 10000); // 10 second timeout

        return () => clearTimeout(timeoutId);
      }
    };

    // Set up message listener
    window.addEventListener('message', handleMessage);

    // Check session every 30 seconds (only if not in failed state)
    const intervalId = setInterval(() => {
      if (!isAuthFailed) {
        checkSession();
      }
    }, 30000);

    // Set up loading timeout
    const clearLoadingTimeout = setupLoadingTimeout();

    // Cleanup
    return () => {
      window.removeEventListener('message', handleMessage);
      clearInterval(intervalId);
      if (clearLoadingTimeout) clearLoadingTimeout();
    };
  }, [isSlideoutOpen, iframeLoading, isAuthFailed, retryCount]);

  // Handle ESC key to close full window
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isFullWindowOpen) {
        setIsFullWindowOpen(false);
      }
    };

    if (isFullWindowOpen) {
      document.addEventListener('keydown', handleEscKey);
      return () => document.removeEventListener('keydown', handleEscKey);
    }
  }, [isFullWindowOpen]);

  // Hide main content when full window is open
  useEffect(() => {
    if (isFullWindowOpen) {
      // Find and hide the main element
      const mainElement = document.querySelector('main');
      const bodyElement = document.body;

      if (mainElement) {
        mainElement.style.display = 'none';
      }

      // Also hide other potential root elements
      const rootElements = document.querySelectorAll('div[id="__next"], div[class*="app"], div[class*="root"]');
      const originalDisplays: string[] = [];

      rootElements.forEach((element, index) => {
        if (element !== mainElement) {
          originalDisplays[index] = (element as HTMLElement).style.display;
          (element as HTMLElement).style.display = 'none';
        }
      });

      // Hide body overflow
      bodyElement.style.overflow = 'hidden';

      return () => {
        // Restore main element
        if (mainElement) {
          mainElement.style.display = '';
        }

        // Restore other elements
        rootElements.forEach((element, index) => {
          if (element !== mainElement) {
            (element as HTMLElement).style.display = originalDisplays[index] || '';
          }
        });

        // Restore body overflow
        bodyElement.style.overflow = '';
      };
    }
  }, [isFullWindowOpen]);

  if (!isSlideoutOpen) return null;

  return (
    <>
      {/* Full Window Overlay */}
      {isFullWindowOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            zIndex: 2147483647,
            background: '#FFFFFF',
            display: 'flex',
            flexDirection: 'column',
          }}>

          {/* Header */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: window.innerWidth >= 768 ? '16px 24px' : '12px 16px', // Responsive padding
            borderBottom: '1px solid #E5E7EB',
            background: '#FFFFFF',
            minHeight: window.innerWidth >= 768 ? '64px' : '56px', // Responsive height
            flexShrink: 0,
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
            }}>
              <Image
                className="h-8 w-8"
                src="/images/agent-1-full.png"
                alt="Raiqa Avatar"
                width={32}
                height={32}
              />
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '2px',
              }}>
                <h1 style={{
                  fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                  fontWeight: 600,
                  fontSize: window.innerWidth >= 768 ? '20px' : '18px', // Responsive font size
                  color: '#020617',
                  margin: 0,
                }}>
                  {window.innerWidth >= 768 ? 'Ask Knowlan - Clinical Knowledge Assistant' : 'Ask Knowlan'} {/* Responsive text */}
                </h1>
                {window.innerWidth >= 768 && (
                  <p style={{
                    fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                    fontSize: '14px',
                    color: '#64748B',
                    margin: 0,
                    lineHeight: '1.4',
                  }}>
                    Surfacing clinical updates, journal highlights & curated learning
                  </p>
                )}
              </div>
            </div>

            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}>
              {/* Minimize Button */}
              <button
                onClick={() => {
                  setIsFullWindowOpen(false);
                  // Don't restore sidebar when minimizing, only when closing
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: window.innerWidth >= 768 ? '6px' : '4px',
                  padding: window.innerWidth >= 768 ? '8px 12px' : '10px 14px', // Better touch targets
                  background: '#F3F4F6',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: window.innerWidth >= 768 ? '14px' : '13px',
                  fontWeight: 500,
                  color: '#374151',
                  minHeight: '44px', // Minimum touch target size
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#E5E7EB'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#F3F4F6'}
                title="Minimize to slideout">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M8 3H5a2 2 0 00-2 2v3m18 0V5a2 2 0 00-2-2h-3m0 18h3a2 2 0 002-2v-3M3 16v3a2 2 0 002 2h3"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    transform="rotate(180 12 12)"
                  />
                </svg>
                Minimize
              </button>

              {/* Close Button */}
              <button
                onClick={() => {
                  setIsFullWindowOpen(false);
                  setIsSlideoutOpen(false);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: window.innerWidth >= 768 ? '6px' : '4px',
                  padding: window.innerWidth >= 768 ? '8px 12px' : '10px 14px', // Better touch targets
                  background: '#FEF2F2',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: window.innerWidth >= 768 ? '14px' : '13px',
                  fontWeight: 500,
                  color: '#DC2626',
                  minHeight: '44px', // Minimum touch target size
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#FEE2E2'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#FEF2F2'}
                title="Close chat">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M18 6L6 18M6 6l12 12"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Close
              </button>
            </div>
          </div>

          {/* Iframe */}
          <div style={{
            flex: 1,
            overflow: 'hidden',
            background: '#FFFFFF',
          }}>
            {isAuthFailed ? (
              <div style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#6B7280',
                fontSize: '18px',
                background: '#FFFFFF',
                padding: '40px',
                textAlign: 'center',
              }}>
                <div style={{
                  marginBottom: '20px',
                  fontSize: '24px',
                  color: '#DC2626',
                }}>
                  Authentication Failed
                </div>
                <div style={{
                  marginBottom: '30px',
                  fontSize: '16px',
                  color: '#6B7280',
                  lineHeight: '1.5',
                }}>
                  Unable to authenticate with the AI Assistant after {MAX_RETRIES} attempts.
                  <br />
                  Please check your connection and try again.
                </div>
                <button
                  onClick={() => {
                    setIsAuthFailed(false);
                    setRetryCount(0);
                    setLastRetryTime(0);
                    setShouldReprocessUrl(true);
                  }}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: '#3B82F6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '16px',
                    cursor: 'pointer',
                    fontWeight: '500',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2563EB'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#3B82F6'}
                >
                  Try Again
                </button>
              </div>
            ) : iframeUrl ? (
              <iframe
                id="knowlen-chat-iframe-fullwindow"
                src={iframeUrl}
                allow="microphone *; camera *; geolocation *; display-capture *; autoplay *; encrypted-media *; fullscreen *"
                referrerPolicy="strict-origin-when-cross-origin"
                sandbox="allow-same-origin allow-scripts allow-popups allow-forms allow-modals allow-orientation-lock allow-pointer-lock allow-presentation allow-top-navigation-by-user-activation"
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none',
                  display: 'block',
                }}
                title="Raiqa AI Assistant - Full Window"
                onLoad={() => {
                  console.log('Full window iframe loaded');
                  setIframeLoading(false);
                  // Reset retry count on successful load
                  setRetryCount(0);
                  setIsAuthFailed(false);
                }}
                onError={() => {
                  console.log('Full window iframe failed to load, will retry with SSO');
                  setIframeLoading(false);
                  handleAuthFailure();
                }}
              />
            ) : (
              <div style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#6B7280',
                fontSize: '18px',
                background: '#FFFFFF',
              }}>
                Loading AI Assistant...
              </div>
            )}
          </div>
        </div>
      )}

      {/* Slideout Panel */}
      <div
        className={`fixed top-0 right-0 h-full transition-all duration-300 ease-in-out ${
          isSlideoutOpen ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'
        }`}
        style={{
          width: '428px',
          height: '100vh',
          background: '#F8FAFC',
          display: isFullWindowOpen ? 'none' : 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          padding: '24px',
          gap: '16px',
          boxShadow: isSlideoutOpen ? '-10px 0 30px rgba(0, 0, 0, 0.3)' : 'none',
          transform: isSlideoutOpen ? 'translateX(0)' : 'translateX(100%)',
          zIndex: 9999,
        }}>

        {/* Header */}
        <div style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '8px 0px',
          gap: '32px',
          width: '380px',
          height: '48px',
        }}>
          <div style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            padding: '0px',
            gap: '32px',
            width: '380px',
            height: '32px',
            flexGrow: 1,
          }}>

            {/* Title */}
            <div style={{
              width: '276px',
              flexDirection: 'column',
              flexGrow: 1,
            }}>
              <h2 style={{
                fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                fontStyle: 'normal',
                fontWeight: 600,
                fontSize: '24px',
                lineHeight: '100%',
                color: '#020617',
                margin: 0,
                marginBottom: '4px',
              }}>
                Ask Knowlan
              </h2>
              <p style={{
                fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
                fontSize: '12px',
                lineHeight: '140%',
                color: '#64748B',
                margin: 0,
              }}>
                Clinical updates, journal highlights & curated learning
              </p>
            </div>

            {/* Action Buttons */}
            <div style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              padding: '0px',
              gap: '8px',
              width: '72px',
              height: '32px',
            }}>
              {/* Maximize Button */}
              <button
                onClick={() => setIsFullWindowOpen(true)}
                style={{
                  width: '32px',
                  height: '32px',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                title="Open in full window">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M8 3H5a2 2 0 00-2 2v3m18 0V5a2 2 0 00-2-2h-3m0 18h3a2 2 0 002-2v-3M3 16v3a2 2 0 002 2h3"
                    stroke="#020617"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>

              {/* Close Button */}
              <button
                onClick={() => {
                  setIsSlideoutOpen(false);
                }}
                style={{
                  width: '32px',
                  height: '32px',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                title="Close">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M18 6L6 18M6 6l12 12"
                    stroke="#020617"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Iframe Container */}
        <div style={{
          width: '380px',
          height: 'calc(100vh - 120px)',
          background: '#FFFFFF',
          border: '1px solid #CBD5E1',
          borderRadius: '12px',
          overflow: 'hidden',
          flexGrow: 1,
        }}>
          {isAuthFailed ? (
            <div style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#6B7280',
              fontSize: '14px',
              padding: '20px',
              textAlign: 'center',
            }}>
              <div style={{
                marginBottom: '12px',
                fontSize: '16px',
                color: '#DC2626',
                fontWeight: '600',
              }}>
                Authentication Failed
              </div>
              <div style={{
                marginBottom: '20px',
                fontSize: '12px',
                color: '#6B7280',
                lineHeight: '1.4',
              }}>
                Unable to authenticate after {MAX_RETRIES} attempts.
                <br />
                Please try again.
              </div>
              <button
                onClick={() => {
                  setIsAuthFailed(false);
                  setRetryCount(0);
                  setLastRetryTime(0);
                  setShouldReprocessUrl(true);
                }}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#3B82F6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '12px',
                  cursor: 'pointer',
                  fontWeight: '500',
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2563EB'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#3B82F6'}
              >
                Try Again
              </button>
            </div>
          ) : iframeUrl ? (
                          <iframe
                id="knowlen-chat-iframe"
                src={iframeUrl}
                allow="microphone *; camera *; geolocation *; display-capture *; autoplay *; encrypted-media *; fullscreen *"
                referrerPolicy="strict-origin-when-cross-origin"
                sandbox="allow-same-origin allow-scripts allow-popups allow-forms allow-modals allow-orientation-lock allow-pointer-lock allow-presentation allow-top-navigation-by-user-activation"
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none',
                  borderRadius: '12px',
                }}
                title="Knowlan AI Assistant"
                onLoad={() => {
                  console.log('Slideout iframe loaded');
                  setIframeLoading(false);
                  // Reset retry count on successful load
                  setRetryCount(0);
                  setIsAuthFailed(false);
                }}
                onError={() => {
                  console.log('Slideout iframe failed to load, will retry with SSO');
                  setIframeLoading(false);
                  handleAuthFailure();
                }}
              />
          ) : (
            <div style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#6B7280',
              fontSize: '14px',
            }}>
              Loading AI Assistant...
            </div>
          )}
        </div>
      </div>
    </>
  );
};

import { useLocale } from "@calcom/lib/hooks/useLocale";
import useMeQuery from "@calcom/trpc/react/hooks/useMeQuery";
import { Avatar } from "@calcom/ui/components/avatar";
import {
  Dropdown,
  DropdownItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuTrigger,
} from "@calcom/ui/components/dropdown";
import { Icon } from "@calcom/ui/components/icon";
import { Logo } from "@calcom/ui/components/logo";

export const FocusModesDropdown = () => {
  const { focusMode, setFocusMode } = useFocusMode();

  const focusModes = [
    { value: "Consultations" as const, label: "Consultations"},
    { value: "Catch-ups" as const, label: "Catch-ups"},
  ];

  return (
    <Dropdown>
      <DropdownMenuTrigger asChild>
        <button
          className="flex items-center justify-center gap-2 rounded-full"
          type="button"
          style={{
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
            alignItems: "center",
            padding: "14px 16px",
            gap: "8px",
            width: "175px",
            height: "50px",
            background: "#FFFFFF",
            border: "1px solid #CBD5E1",
            borderRadius: "35px",
            flex: "none",
            flexGrow: 0,
          }}>
          {/* SVG Icon */}
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{
              flex: "none",
              order: 0,
              flexGrow: 0,
            }}>
            <path d="M7.08594 8.60938C7.08594 8.77769 7.22231 8.91406 7.39063 8.91406H7.61914V15.6953C7.61911 15.8636 7.48268 15.9999 7.31445 16H0.267578C0.119933 16 0 15.8801 0 15.7324C0.000140949 11.7613 3.14893 8.52764 7.08594 8.3877V8.60938ZM8.91406 8.3877C12.8511 8.52763 15.9999 11.7613 16 15.7324C16 15.8801 15.8801 16 15.7324 16H8.68555C8.51733 15.9999 8.38089 15.8635 8.38086 15.6953V8.91406H8.60938C8.77769 8.91406 8.91406 8.77769 8.91406 8.60938V8.3877ZM7.31445 0C7.48267 8.44826e-05 7.6191 0.136459 7.61914 0.304688V7.08594H7.39063C7.22231 7.08594 7.08594 7.22231 7.08594 7.39063V7.61133C3.14894 7.47137 0.0001283 4.2387 0 0.267578C0 0.119933 0.119933 0 0.267578 0H7.31445ZM15.7324 0C15.8801 0 16 0.119933 16 0.267578C15.9999 4.23871 12.8511 7.47138 8.91406 7.61133V7.39063C8.91406 7.22231 8.77769 7.08594 8.60938 7.08594H8.38086V0.304688C8.3809 0.136467 8.51734 9.74843e-05 8.68555 0H15.7324Z" fill="url(#paint0_linear_780_48938)"/>
            <defs>
              <linearGradient id="paint0_linear_780_48938" x1="-0.499999" y1="2.31405" x2="10.5887" y2="16.2092" gradientUnits="userSpaceOnUse">
                <stop stopColor="#FFA537"/>
                <stop offset="0.25" stopColor="#FF7139"/>
                <stop offset="0.5" stopColor="#FD2C3F"/>
                <stop offset="0.755" stopColor="#FF2185"/>
                <stop offset="1" stopColor="#B830E1"/>
              </linearGradient>
            </defs>
          </svg>
          {/* Text */}
          <span
            style={{
              width: "95px",
              height: "22px",
              fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
              fontStyle: "normal",
              fontWeight: 600,
              fontSize: "14px",
              lineHeight: "22px",
              display: "flex",
              alignItems: "center",
              background: "linear-gradient(141.41deg, #FFA537 6.66%, #FF7139 26.42%, #FD2C3F 46.19%, #FF2185 66.35%, #B830E1 85.72%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              flex: "none",
              order: 1,
              flexGrow: 0,
            } as React.CSSProperties}>
            {focusMode}
          </span>
          {/* Chevron Down */}
          <Icon
            name="chevron-down"
            style={{
              width: "16px",
              height: "16px",
              flex: "none",
              order: 2,
              flexGrow: 0,
            }}
          />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuPortal>
        <DropdownMenuContent
          className="z-[9999] w-56 p-2 bg-white border border-gray-200 rounded-xl shadow-lg"
          style={{
            zIndex: 9999,
            position: "fixed",
            backgroundColor: "white",
            borderRadius: "12px",
            border: "1px solid #E5E7EB",
            boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
            padding: "8px",
          }}
          align="start"
          side="bottom"
          sideOffset={5}>
          {focusModes.map((mode) => (
            <DropdownMenuItem
              key={mode.value}
              className="p-0 focus:bg-transparent"
              style={{ padding: 0 }}>
              <DropdownItem
                type="button"
                onClick={() => setFocusMode(mode.value)}
                className="w-full p-3 rounded-lg hover:bg-gray-50 transition-colors"
                style={{
                  width: "100%",
                  padding: "12px",
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  background: "transparent",
                  border: "none",
                  cursor: "pointer"
                }}>
                <div className="flex w-full items-center justify-between">
                  <span
                    className="text-sm font-medium text-gray-900"
                    style={{
                      fontSize: "14px",
                      fontWeight: 500,
                      color: "#111827"
                    }}>
                    {mode.label}
                  </span>
                </div>
              </DropdownItem>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenuPortal>
    </Dropdown>
  );
};

const UserHeaderDropdown = () => {
  const { data: user, isPending } = useMeQuery();
  const { t } = useLocale(); 

  if (isPending) {
    return <div className="h-10 w-48 animate-pulse rounded-lg bg-gray-200" />;
  }

  return (
    <Dropdown>
      <DropdownMenuTrigger asChild>
        <button
          className="bg-muted group flex items-center space-x-2 rounded-full p-2 text-left transition-colors"
          data-testid="user-dropdown">
          <Avatar
            size="md"
            imageSrc={user?.avatar.includes("avatar.svg") ? "/images/profile-avatar.png" : user?.avatar}
            alt={user?.name || "User avatar"}
          />
          <div className="flex-grow">
            <div className="text-default text-sm font-semibold">{user?.name}</div>
            <div className="text-default text-xs">{user?.email}</div>
          </div>
          <Icon name="chevron-down" className="h-4 w-4 text-gray-500" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuPortal>
        <DropdownMenuContent
          className="z-[9999]"
          style={{
            zIndex: 9999,
            position: "fixed"
          }}
          side="bottom"
          align="start"
          sideOffset={5}>
          <DropdownMenuItem>
            <DropdownItem
              type="button"
              StartIcon="log-out"
              onClick={() => signOut({ callbackUrl: "/auth/logout" })}>
              {t("sign_out")}
            </DropdownItem>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenuPortal>
    </Dropdown>
  );
};

export const Header = () => {
  const { isSlideoutOpen, setIsSlideoutOpen, isSidebarCollapsed, setIsSidebarCollapsed, isFullWindowOpen, setIsFullWindowOpen } = useSlideoutContext();
  const pathname = usePathname();
  const { data: session } = useSession();
  const { pmsId, setPmsId, isLoadingPms } = usePmsId();

  // Check if user is platform practitioner (standalone, no team access)
  const isPlatformPractitioner = (session?.user as any)?.userCategory === "PRACTITIONER" && (session?.user as any)?.userType === "PLATFORM";

  const openRaiqa = async () => {
    // Try to request microphone permissions proactively
    try {
      if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
        console.log('Requesting microphone permissions for parent domain...');
        await navigator.mediaDevices.getUserMedia({ audio: true });
        console.log('Microphone permissions granted on parent domain');
      }
    } catch (error) {
      console.log('Microphone permission request on parent domain:', error);
      // Continue opening the assistant even if permission fails
    }

    // Check if we're on a medium screen (typically tablets, smaller laptops)
    const isDesktop = window.innerWidth >= 1280; // lg breakpoint

    if (isDesktop) {
      // Desktop: Open in slideout mode and collapse sidebar
      setIsSlideoutOpen(true);
      setIsSidebarCollapsed(true);
    } else {
      // Medium/Small screens: Open directly in full window mode
      setIsSlideoutOpen(true);
      setIsSidebarCollapsed(true);
      // Small delay to ensure slideout is initialized first
      setTimeout(() => {
        setIsFullWindowOpen(true);
      }, 100);
    }
  };



  // Handle window resize to automatically switch modes if needed
  useEffect(() => {
    const handleResize = () => {
      const isDesktop = window.innerWidth >= 1024;

      // If currently in slideout mode on a small screen, switch to full window
      if (isSlideoutOpen && !isFullWindowOpen && !isDesktop) {
        setIsFullWindowOpen(true);
      }
      // If currently in full window mode on desktop, offer to switch to slideout
      else if (isFullWindowOpen && isDesktop) {
        // Could add logic here to automatically minimize to slideout
        // For now, we'll let users manually control this
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isSlideoutOpen, isFullWindowOpen]);

  // Prevent any title interference with external windows
  useEffect(() => {
    // Store original document title
    const originalTitle = document.title;

    // Cleanup function to restore title if needed
    return () => {
      // Only restore if title was changed by this component
      if (document.title !== originalTitle && originalTitle) {
        document.title = originalTitle;
      }
    };
  }, []);

  // Restore sidebar when slideout is closed
  useEffect(() => {
    if (window.innerWidth <= 1920 && !isSlideoutOpen && isSidebarCollapsed) {
      setIsSidebarCollapsed(true);
    }
  }, [isSlideoutOpen, isSidebarCollapsed, setIsSidebarCollapsed]);

  // Don't render header when in full window mode
  if (isFullWindowOpen) {
    return <RaiqaSlideout />;
  }

  return (
    <>
      <header
        className={`flex h-12 items-center justify-between pt-10 pb-10 ${isSlideoutOpen ? 'z-0' : 'z-10'}`}
        style={{ zIndex: isSlideoutOpen ? 0 : 10 }}>
        <div className="flex items-center space-x-2">
          {/*<Logo />*/}
          {/*<span className="text-xl font-bold">Raiqa Health</span>*/}
          {/*<Button variant="icon" color="minimal" StartIcon="chevrons-left" />*/}
        </div>
        <div className="flex items-center space-x-2">
          {/* PMS Button - Only show if pmsId exists */}
          {!isLoadingPms && pmsId && (
            <button
              className="relative border-muted flex items-center gap-2 rounded-full border-2 bg-white py-1 pl-1 pr-4 transition-all duration-200 hover:shadow-md active:scale-95 md:min-h-0 min-h-[48px] md:py-1 md:pl-1 md:pr-4"
              onClick={() => window.open('https://app.medirecords.com', '_blank')}
              type="button"
              aria-label="Open PMS"
              title="Open PMS in new tab">

            <svg className="absolute top-0 right-0 h-5 w-5 text-gray-500 bg-white rounded-full p-1 border border-gray-200 shadow-sm" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4.25 5.5a.75.75 0 00-.75.75v8.5c0 .414.336.75.75.75h8.5a.75.75 0 00.75-.75v-4a.75.75 0 011.5 0v4A2.25 2.25 0 0112.75 17h-8.5A2.25 2.25 0 012 14.75v-8.5A2.25 2.25 0 014.25 4h5a.75.75 0 010 1.5h-5z" clipRule="evenodd" />
              <path fillRule="evenodd" d="M6.194 12.753a.75.75 0 001.06.053L16.5 4.44v2.81a.75.75 0 001.5 0v-4.5a.75.75 0 00-.75-.75h-4.5a.75.75 0 000 1.5h2.553l-9.056 8.194a.75.75 0 00-.053 1.06z" clipRule="evenodd" />
            </svg>

            <div className="relative h-9 w-9 flex-shrink-0 bg-white rounded-full flex items-center justify-center">
              <Image
                src="/images/mr-logo.png"
                alt="My PMS"
                width={24}
                height={24}
                className="h-24 w-24 object-contain"
              />
            </div>

            <span className="text-sm font-medium text-black xl:block hidden">
              My PMS
            </span>
            <span className="text-sm font-medium text-black xl:hidden block">
              PMS
            </span>
          </button>
          )}

          {/* Microsoft Teams Button - Only show for non-platform practitioners */}
          {!isPlatformPractitioner && (
            <button
              className="relative border-muted flex items-center gap-2 rounded-full border-2 bg-white py-1 pl-1 pr-4 transition-all duration-200 hover:shadow-md active:scale-95 md:min-h-0 min-h-[48px] md:py-1 md:pl-1 md:pr-4"
              onClick={() => window.open('https://teams.microsoft.com', '_blank')}
              type="button"
              aria-label="Open Microsoft Teams"
              title="Open Microsoft Teams in new tab">

              {/* New tab indicator */}
              <svg className="absolute top-0 right-0 h-5 w-5 text-gray-500 bg-white rounded-full p-1 border border-gray-200 shadow-sm" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4.25 5.5a.75.75 0 00-.75.75v8.5c0 .414.336.75.75.75h8.5a.75.75 0 00.75-.75v-4a.75.75 0 011.5 0v4A2.25 2.25 0 0112.75 17h-8.5A2.25 2.25 0 012 14.75v-8.5A2.25 2.25 0 014.25 4h5a.75.75 0 010 1.5h-5z" clipRule="evenodd" />
                <path fillRule="evenodd" d="M6.194 12.753a.75.75 0 001.06.053L16.5 4.44v2.81a.75.75 0 001.5 0v-4.5a.75.75 0 00-.75-.75h-4.5a.75.75 0 000 1.5h2.553l-9.056 8.194a.75.75 0 00-.053 1.06z" clipRule="evenodd" />
              </svg>

              <div className="relative h-9 w-9 flex-shrink-0 bg-white rounded-full flex items-center justify-center">
                <Image
                  src="/Microsoft_Office_Teams_Logo_256px.png"
                  alt="Microsoft Teams"
                  width={24}
                  height={24}
                  className="h-6 w-6 object-contain"
                />
              </div>

              <span className="text-sm font-medium text-black xl:block hidden">
                Collaborate
              </span>
              <span className="text-sm font-medium text-black xl:hidden block">
                Teams
              </span>
            </button>
          )}

          {/* Outlook Button - Only show for non-platform practitioners */}
          {!isPlatformPractitioner && (
            <button
              className="relative border-muted flex items-center gap-2 rounded-full border-2 bg-white py-1 pl-1 pr-4 transition-all duration-200 hover:shadow-md active:scale-95 md:min-h-0 min-h-[48px] md:py-1 md:pl-1 md:pr-4"
              onClick={() => window.open('https://outlook.office.com', '_blank')}
              type="button"
              aria-label="Open Outlook"
              title="Open Outlook in new tab">

              {/* New tab indicator */}
              <svg className="absolute top-0 right-0 h-5 w-5 text-gray-500 bg-white rounded-full p-1 border border-gray-200 shadow-sm" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4.25 5.5a.75.75 0 00-.75.75v8.5c0 .414.336.75.75.75h8.5a.75.75 0 00.75-.75v-4a.75.75 0 011.5 0v4A2.25 2.25 0 0112.75 17h-8.5A2.25 2.25 0 012 14.75v-8.5A2.25 2.25 0 014.25 4h5a.75.75 0 010 1.5h-5z" clipRule="evenodd" />
                <path fillRule="evenodd" d="M6.194 12.753a.75.75 0 001.06.053L16.5 4.44v2.81a.75.75 0 001.5 0v-4.5a.75.75 0 00-.75-.75h-4.5a.75.75 0 000 1.5h2.553l-9.056 8.194a.75.75 0 00-.053 1.06z" clipRule="evenodd" />
              </svg>

              <div className="relative h-9 w-9 flex-shrink-0 bg-white rounded-full flex items-center justify-center">
                <Image
                  src="/Microsoft_Office_Outlook_Logo_256px.png"
                  alt="Microsoft Outlook"
                  width={24}
                  height={24}
                  className="h-6 w-6 object-contain"
                />
              </div>

              <span className="text-sm font-medium text-black xl:block hidden">
                Inbox
              </span>
              <span className="text-sm font-medium text-black xl:hidden block">
                Mail
              </span>
            </button>
          )}

          {/* Ask AI Button */}
          <button
            className="border-muted flex items-center gap-2 rounded-full border-2 bg-white py-1 pl-1 pr-4 transition-all duration-200 hover:shadow-md active:scale-95 md:min-h-0 min-h-[48px] md:py-1 md:pl-1 md:pr-4"
            onClick={openRaiqa}
            type="button"
            aria-label="Ask Knowlan - Clinical Knowledge Assistant"
            title="This assistant keeps you sharp — surfacing relevant clinical updates, journal highlights, and curated learning that fits your specialty and interests.">

            <div className="relative h-9 w-9 flex-shrink-0">
              <Image className="h-9 w-9 absolute rounded-full z-40" src="/images/agent-1-full.png" alt="Knowlan Avatar" width={36} height={36} />
              <video
                src="/videos/raiqa_ai_symbol.mp4"
                autoPlay
                loop
                muted
                disablePictureInPicture
                className="h-9 w-9 rounded-full object-cover z-30"
              />
            </div>

            <span className="text-sm font-medium bg-gradient-to-r from-red-500 via-pink-500 to-purple-500 bg-clip-text text-transparent xl:block hidden">
              Ask Knowlan
            </span>
            <span className="text-sm font-medium bg-gradient-to-r from-red-500 via-pink-500 to-purple-500 bg-clip-text text-transparent xl:hidden block">
              Knowlan
            </span>
          </button>

          <UserHeaderDropdown />
        </div>
      </header>
      <RaiqaSlideout />
    </>
  );
};

export const HeaderContainer = () => {
  const { status } = useSession();
  if (status !== "authenticated") return null;
  return <Header />;
};
